import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Drawer,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Chip,
  Avatar,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Checkbox,
  ListItemText,
  CircularProgress,
  Divider,
  Stack,
  Tabs,
  Tab,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  VerifiedUser as VerifiedUserIcon,
  PersonOff as PersonOffIcon,
  Business as BusinessIcon,
  ViewModule as ViewModuleIcon,
  TableRows as TableRowsIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatDateTime, formatJalaliDateDisplay } from '../utils/dateUtils';
import { API_BASE_URL } from '../config';
import { authService } from '../services/authService';
import {
  TABLE_HEADER_BOX_SX,
  TABLE_HEAD_CELL_SX,
  SECTION_TITLE_SX,
  tableRowSx,
} from '../styles/reportPageStyles';
import ListPagination from './ListPagination';

const CARD_SX = {
  borderRadius: 3,
  border: '1px solid rgba(0, 168, 106, 0.1)',
  boxShadow: '0 8px 32px rgba(50, 51, 56, 0.06)',
  bgcolor: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(8px)',
};

const KPI_COLORS = ['#00a86a', '#0073ea', '#00a86a', '#fdab3d'];

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
  },
};

const USER_CARDS_GRID = {
  display: 'grid',
  gap: 2,
  width: '100%',
  direction: 'rtl',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(auto-fit, minmax(300px, 1fr))',
  },
};

const fmt = (n) => (Number(n) || 0).toLocaleString('fa-IR');

const AUDIT_GRID_COLS = '140px 100px 120px 100px 1fr 1fr 1fr 1fr 1fr 100px 90px 80px 1fr 1fr 70px';

const normalizeEntrySnapshot = (data) => {
  if (!data || typeof data !== 'object') return null;
  const hasEntryFields = data.company || data.product || data.documentNumber || data.document_number;
  if (!hasEntryFields) return null;
  return {
    company: data.company || '—',
    product: data.product || '—',
    documentType: data.documentType || data.document_type || '—',
    documentNumber: data.documentNumber || data.document_number || '—',
    location: data.location || '—',
    shift: data.shift || '—',
    date: data.date || null,
    quantity: data.quantity ?? '—',
    unit: data.unit || 'کیلوگرم',
    referenceToWaybill: data.referenceToWaybill || data.reference_to_waybill || '—',
    description: data.description || '',
    assayPercent: data.assayPercent ?? data.assay_percent,
  };
};

const flattenAuditLogs = (logs) => {
  const rows = [];
  (logs || []).forEach((log) => {
    const parsed = log.details_parsed || {};
    const bulkEntries = parsed.entries;
    if (Array.isArray(bulkEntries) && bulkEntries.length) {
      bulkEntries.forEach((entry, idx) => {
        rows.push({
          ...log,
          rowKey: `${log.id}-e${idx}`,
          entry: normalizeEntrySnapshot(entry),
          meta: parsed,
        });
      });
      return;
    }
    const snapshot = log.snapshot || parsed.snapshot || parsed;
    rows.push({
      ...log,
      rowKey: String(log.id),
      entry: normalizeEntrySnapshot(snapshot),
      meta: parsed,
    });
  });
  return rows;
};

const formatOtherAuditInfo = (row) => {
  if (row.entry) return null;
  const s = row.snapshot || row.meta?.snapshot || row.meta;
  if (!s) return row.details || '—';
  if (row.entity_type === 'user') {
    return [
      s.username && `کاربر: ${s.username}`,
      (s.first_name || s.last_name) && `نام: ${`${s.first_name || ''} ${s.last_name || ''}`.trim()}`,
      s.role && `نقش: ${s.role === 'admin' ? 'مدیر' : 'کاربر'}`,
      s.is_super_admin != null && `سوپر ادمین: ${s.is_super_admin ? 'بله' : 'خیر'}`,
      s.is_active != null && `وضعیت: ${s.is_active ? 'فعال' : 'غیرفعال'}`,
      s.company_names?.length && `شرکت‌ها: ${s.company_names.join('، ')}`,
      row.meta?.changes && `تغییرات: ${JSON.stringify(row.meta.changes)}`,
    ].filter(Boolean).join(' | ');
  }
  if (s.name) {
    let text = s.name;
    if (s.requiresAssay != null) text += ` | عیارسنجی: ${s.requiresAssay ? 'بله' : 'خیر'}`;
    if (s.assayPercent != null) text += ` | عیار: ${s.assayPercent}`;
    if (row.meta?.from) text += ` | ${row.meta.from} → ${row.meta.to}`;
    return text;
  }
  if (row.meta?.from) return `${row.meta.from} → ${row.meta.to}`;
  if (row.meta?.changes) return JSON.stringify(row.meta.changes);
  return typeof s === 'object' ? JSON.stringify(s) : String(s);
};

const getInitials = (user) => {
  const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
  if (name) return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('');
  return (user?.username || 'ک')[0]?.toUpperCase() || 'ک';
};

const KpiCard = ({ icon, label, value, color, sub }) => (
  <Card sx={{ ...CARD_SX, height: '100%' }}>
    <Box sx={{ height: 4, bgcolor: color }} />
    <CardContent sx={{ py: 2, px: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#676879', fontWeight: 600 }}>{label}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#323338', mt: 0.5 }}>{value}</Typography>
          {sub && <Typography variant="caption" sx={{ color: '#c5c7d0' }}>{sub}</Typography>}
        </Box>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: `${color}18`, color,
        }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ModernDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'user',
    is_active: true,
    is_super_admin: false,
    company_ids: [],
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilters, setRoleFilters] = useState([]);
  const [viewMode, setViewMode] = useState('cards');
  const [activeTab, setActiveTab] = useState(() => (
    searchParams.get('tab') === 'audits' ? 'audits' : 'users'
  ));
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditQuery, setAuditQuery] = useState('');
  const [auditPage, setAuditPage] = useState(0);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditDescOpen, setAuditDescOpen] = useState(false);
  const [auditDescText, setAuditDescText] = useState('');
  const [usersPage, setUsersPage] = useState(0);
  const [usersPageSize] = useState(8);
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab') === 'audits' ? 'audits' : 'users';
    setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (_e, value) => {
    setActiveTab(value);
    if (value === 'audits') {
      setSearchParams({ tab: 'audits' });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    if (currentUser && !currentUser.is_super_admin && activeTab === 'audits') {
      setActiveTab('users');
      setSearchParams({});
    }
  }, [currentUser, activeTab, setSearchParams]);

  const refreshCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const user = response.data;
      setCurrentUser(user);
      authService.setCurrentUser(user);
      return user;
    } catch (error) {
      console.error('Error fetching current user:', error);
      const cached = authService.getCurrentUser();
      if (cached) setCurrentUser(cached);
      return cached;
    }
  };

  useEffect(() => {
    const init = async () => {
      const user = await refreshCurrentUser();
      if (!user || user.role !== 'admin') {
        navigate('/entry');
        return;
      }
      fetchUsers();
      fetchCompanies();
      setQuery('');
      setStatusFilter('all');
      setRoleFilters([]);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/companies/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('خطا در دریافت لیست کاربران', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async (page = 0, search = auditQuery) => {
    if (!currentUser?.is_super_admin) return;
    setAuditLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/auth/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: page + 1,
          per_page: 50,
          q: search.trim() || undefined,
        },
      });
      setAuditLogs(response.data?.items || []);
      setAuditTotal(response.data?.total || 0);
      setAuditPage(page);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      showSnackbar('خطا در دریافت سوابق عملیات', 'error');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'audits' && currentUser?.is_super_admin) {
      fetchAuditLogs(auditPage, auditQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentUser?.is_super_admin]);

  useEffect(() => {
    if (activeTab === 'audits' && currentUser?.is_super_admin && auditLogs.length === 0 && !auditLoading) {
      fetchAuditLogs(0, auditQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const normalizeCompanyIds = (ids = []) =>
    (Array.isArray(ids) ? ids : [])
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));

  const emptyForm = () => ({
    username: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'user',
    is_active: true,
    is_super_admin: false,
    company_ids: [],
  });

  const toFormData = (user = null) => ({
    username: user?.username || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    password: '',
    role: user?.role || 'user',
    is_active: user?.is_active ?? true,
    is_super_admin: Boolean(user?.is_super_admin),
    company_ids: normalizeCompanyIds(user?.company_ids),
  });

  const editingUser = useMemo(
    () => (editingUserId != null
      ? users.find((u) => Number(u.id) === Number(editingUserId)) || null
      : null),
    [users, editingUserId]
  );

  const resolveUser = (userOrId) => {
    if (userOrId == null) return null;
    if (typeof userOrId === 'object') {
      const id = Number(userOrId.id);
      if (!Number.isNaN(id)) {
        return users.find((u) => Number(u.id) === id) || userOrId;
      }
      return userOrId;
    }
    const id = Number(userOrId);
    return users.find((u) => Number(u.id) === id) || null;
  };

  const openPanel = (userOrId = null) => {
    const resolved = resolveUser(userOrId);
    if (resolved?.id != null) {
      setEditingUserId(Number(resolved.id));
      setFormData(toFormData(resolved));
    } else {
      setEditingUserId(null);
      setFormData(emptyForm());
    }
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setEditingUserId(null);
    setFormData(emptyForm());
  };

  const getCompanyNames = (companyIds = []) => {
    const normalized = normalizeCompanyIds(companyIds);
    return companies
      .filter((c) => normalized.includes(Number(c.id)))
      .map((c) => c.name);
  };

  const companyMenuProps = {
    autoFocus: false,
    disableAutoFocusItem: true,
    PaperProps: { sx: { maxHeight: 280, direction: 'rtl', zIndex: 1600 } },
  };

  const buildUserPayload = () => {
    const payload = {
      username: formData.username?.trim(),
      first_name: formData.first_name?.trim() || '',
      last_name: formData.last_name?.trim() || '',
      role: formData.role,
      is_active: Boolean(formData.is_active),
      company_ids: formData.role === 'admin' ? [] : normalizeCompanyIds(formData.company_ids),
    };
    if (formData.role === 'admin') {
      payload.is_super_admin = Boolean(formData.is_super_admin);
    }
    if (formData.password?.trim()) payload.password = formData.password;
    return payload;
  };

  const handleSubmit = async () => {
    if (formData.role === 'user' && normalizeCompanyIds(formData.company_ids).length === 0) {
      showSnackbar('برای کاربر عادی انتخاب حداقل یک شرکت الزامی است', 'error');
      return;
    }
    if (!editingUserId && !formData.password?.trim()) {
      showSnackbar('رمز عبور الزامی است', 'error');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingUserId
        ? `${API_BASE_URL}/auth/users/${editingUserId}`
        : `${API_BASE_URL}/auth/users`;
      const method = editingUserId ? 'put' : 'post';
      const response = await axios[method](url, buildUserPayload(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (editingUserId && Number(editingUserId) === Number(currentUser?.id)) {
        const updated = response.data;
        setCurrentUser(updated);
        authService.setCurrentUser(updated);
      }
      showSnackbar(editingUserId ? 'کاربر با موفقیت ویرایش شد' : 'کاربر جدید ایجاد شد', 'success');
      closePanel();
      setQuery('');
      setStatusFilter('all');
      setRoleFilters([]);
      window.setTimeout(() => setQuery(''), 100);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      showSnackbar(error.response?.data?.message || 'خطا در ذخیره کاربر', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این کاربر را حذف کنید؟')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/auth/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnackbar('کاربر با موفقیت حذف شد', 'success');
      fetchUsers();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'خطا در حذف کاربر', 'error');
    }
  };

  const hasActiveFilters = Boolean(query.trim() || statusFilter !== 'all' || roleFilters.length > 0);

  const clearFilters = () => {
    setQuery('');
    setStatusFilter('all');
    setRoleFilters([]);
  };

  const filteredUsers = useMemo(() => users.filter((u) => {
    const q = query.trim().toLowerCase();
    const matchesQuery = !q ||
      u.username?.toLowerCase().includes(q) ||
      u.first_name?.toLowerCase().includes(q) ||
      u.last_name?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' ? true : statusFilter === 'active' ? u.is_active : !u.is_active;
    const matchesRole = roleFilters.length === 0 || roleFilters.includes(u.role);
    return matchesQuery && matchesStatus && matchesRole;
  }), [users, query, statusFilter, roleFilters]);

  useEffect(() => {
    setUsersPage(0);
  }, [query, statusFilter, roleFilters, viewMode]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredUsers.length / usersPageSize) - 1);
    if (usersPage > maxPage) setUsersPage(maxPage);
  }, [filteredUsers.length, usersPage, usersPageSize]);

  const pagedUsers = useMemo(() => {
    const start = usersPage * usersPageSize;
    return filteredUsers.slice(start, start + usersPageSize);
  }, [filteredUsers, usersPage, usersPageSize]);

  const stats = useMemo(() => ({
    total: users.length,
    admins: users.filter((u) => u.role === 'admin').length,
    active: users.filter((u) => u.is_active).length,
    inactive: users.filter((u) => !u.is_active).length,
  }), [users]);

  const flattenedAuditRows = useMemo(() => flattenAuditLogs(auditLogs), [auditLogs]);

  const columns = [
    {
      field: 'username',
      headerName: 'کاربر',
      flex: 1.2,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          <Avatar sx={{
            width: 36, height: 36, fontSize: '0.85rem', fontWeight: 800,
            bgcolor: row.role === 'admin' ? '#0073ea' : '#0073ea',
          }}>
            {getInitials(row)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#323338' }}>{row.username}</Typography>
            <Typography variant="caption" sx={{ color: '#676879' }}>{row.first_name} {row.last_name}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'companies',
      headerName: 'شرکت‌ها',
      flex: 1.3,
      sortable: false,
      valueGetter: (_v, row) => {
        if (row?.role === 'admin') return 'همه شرکت‌ها';
        return getCompanyNames(row?.company_ids || []).join('، ') || '—';
      },
    },
    {
      field: 'role',
      headerName: 'نقش',
      width: 120,
      renderCell: ({ value, row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Chip
            size="small"
            label={value === 'admin' ? 'مدیر' : 'کاربر'}
            sx={{
              fontWeight: 700,
              bgcolor: value === 'admin' ? '#e5f4ff' : '#e5f4ff',
              color: value === 'admin' ? '#0073ea' : '#0060c2',
            }}
          />
          {row.is_super_admin && (
            <Chip
              size="small"
              icon={<SecurityIcon sx={{ fontSize: '14px !important' }} />}
              label="سوپر ادمین"
              sx={{ fontWeight: 700, bgcolor: '#e7f4f2', color: '#1a335b', fontSize: '0.7rem' }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'وضعیت',
      width: 100,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value ? 'فعال' : 'غیرفعال'}
          sx={{
            fontWeight: 700,
            bgcolor: value ? '#e7f4f2' : '#fde8eb',
            color: value ? '#037f4c' : '#c9364a',
          }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'تاریخ ایجاد',
      width: 150,
      valueGetter: (_v, row) => (row?.created_at ? formatDateTime(row.created_at) : '—'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="ویرایش">
            <IconButton size="small" onClick={() => openPanel(row.id)} sx={{ color: '#0073ea' }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="حذف">
            <IconButton
              size="small"
              disabled={Number(row.id) === Number(currentUser?.id)}
              onClick={() => handleDelete(row.id)}
              sx={{ color: '#c9364a' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const FilterChip = ({ label, active, onClick, color = '#0073ea' }) => (
    <Chip
      label={label}
      onClick={onClick}
      sx={{
        fontWeight: 700,
        cursor: 'pointer',
        bgcolor: active ? `${color}18` : '#f7f8fa',
        color: active ? color : '#676879',
        border: `1px solid ${active ? `${color}44` : '#e6e9ef'}`,
        '&:hover': { bgcolor: `${color}12` },
      }}
    />
  );

  const UserCard = ({ user: u, wide = false }) => {
    const companyNames = getCompanyNames(u.company_ids || []);
    const isAdmin = u.role === 'admin';

    if (wide) {
      return (
        <Card sx={{
          ...CARD_SX,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': { boxShadow: '0 16px 40px rgba(50, 51, 56,0.1)' },
        }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              gap: { xs: 2, md: 3 },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: { md: 240 } }}>
                <Avatar sx={{
                  width: 56, height: 56, fontWeight: 800, fontSize: '1rem',
                  background: isAdmin
                    ? '#00a86a'
                    : 'linear-gradient(135deg, #0073ea, #00a86a)',
                }}>
                  {getInitials(u)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: '#323338', fontSize: '1.1rem' }}>{u.username}</Typography>
                  <Typography variant="body2" sx={{ color: '#676879' }}>{u.first_name || '—'} {u.last_name || ''}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={isAdmin ? 'مدیر' : 'کاربر'} sx={{ fontWeight: 700, bgcolor: isAdmin ? '#e5f4ff' : '#e5f4ff', color: isAdmin ? '#0073ea' : '#0060c2' }} />
                    {u.is_super_admin && (
                      <Chip size="small" icon={<SecurityIcon sx={{ fontSize: '14px !important' }} />} label="سوپر ادمین" sx={{ fontWeight: 700, bgcolor: '#e7f4f2', color: '#1a335b' }} />
                    )}
                    <Chip size="small" label={u.is_active ? 'فعال' : 'غیرفعال'} sx={{ fontWeight: 700, bgcolor: u.is_active ? '#e7f4f2' : '#fde8eb', color: u.is_active ? '#037f4c' : '#c9364a' }} />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: '#c5c7d0', fontWeight: 600, display: 'block', mb: 0.75 }}>
                  دسترسی شرکت
                </Typography>
                {isAdmin ? (
                  <Chip size="small" icon={<BusinessIcon sx={{ fontSize: 14 }} />} label="همه شرکت‌ها" variant="outlined" sx={{ borderColor: '#cce5ff', color: '#0073ea' }} />
                ) : companyNames.length ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {companyNames.map((name) => (
                      <Chip key={name} size="small" label={name} sx={{ fontSize: '0.75rem', bgcolor: '#f7f8fa', border: '1px solid #e6e9ef' }} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: '#fdab3d' }}>شرکت تعیین نشده</Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, minWidth: { md: 220 } }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => openPanel(u.id)}
                  sx={{ borderRadius: 2, fontWeight: 700, borderColor: '#cce5ff', color: '#0073ea' }}
                >
                  ویرایش
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  disabled={Number(u.id) === Number(currentUser?.id)}
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(u.id)}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  حذف
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{
        ...CARD_SX,
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(50, 51, 56,0.1)' },
      }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Avatar sx={{
              width: 48, height: 48, fontWeight: 800,
              background: isAdmin
                ? '#00a86a'
                : 'linear-gradient(135deg, #0073ea, #00a86a)',
            }}>
              {getInitials(u)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, color: '#323338', fontSize: '1rem' }} noWrap>
                {u.username}
              </Typography>
              <Typography variant="body2" sx={{ color: '#676879' }} noWrap>
                {u.first_name || '—'} {u.last_name || ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={isAdmin ? 'مدیر' : 'کاربر'} sx={{ fontWeight: 700, height: 22, fontSize: '0.7rem', bgcolor: isAdmin ? '#e5f4ff' : '#e5f4ff', color: isAdmin ? '#0073ea' : '#0060c2' }} />
                {u.is_super_admin && (
                  <Chip size="small" icon={<SecurityIcon sx={{ fontSize: '12px !important' }} />} label="سوپر ادمین" sx={{ fontWeight: 700, height: 22, fontSize: '0.65rem', bgcolor: '#e7f4f2', color: '#1a335b' }} />
                )}
                <Chip size="small" label={u.is_active ? 'فعال' : 'غیرفعال'} sx={{ fontWeight: 700, height: 22, fontSize: '0.7rem', bgcolor: u.is_active ? '#e7f4f2' : '#fde8eb', color: u.is_active ? '#037f4c' : '#c9364a' }} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 2, minHeight: 52 }}>
            <Typography variant="caption" sx={{ color: '#c5c7d0', fontWeight: 600, display: 'block', mb: 0.75 }}>
              دسترسی شرکت
            </Typography>
            {isAdmin ? (
              <Chip size="small" icon={<BusinessIcon sx={{ fontSize: 14 }} />} label="همه شرکت‌ها" variant="outlined" sx={{ borderColor: '#cce5ff', color: '#0073ea' }} />
            ) : companyNames.length ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {companyNames.slice(0, 3).map((name) => (
                  <Chip key={name} size="small" label={name} sx={{ fontSize: '0.7rem', bgcolor: '#f7f8fa', border: '1px solid #e6e9ef' }} />
                ))}
                {companyNames.length > 3 && (
                  <Chip size="small" label={`+${companyNames.length - 3}`} sx={{ fontSize: '0.7rem' }} />
                )}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: '#fdab3d' }}>شرکت تعیین نشده</Typography>
            )}
          </Box>

          <Divider sx={{ mb: 1.5 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              onClick={() => openPanel(u.id)}
              sx={{ borderRadius: 2, fontWeight: 700, borderColor: '#cce5ff', color: '#0073ea', '&:hover': { borderColor: '#0073ea', bgcolor: '#e5f4ff' } }}
            >
              ویرایش
            </Button>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="error"
              disabled={Number(u.id) === Number(currentUser?.id)}
              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleDelete(u.id)}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              حذف
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderFormFields = () => (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="caption" sx={{ color: '#676879', fontWeight: 700, mb: 1.5, display: 'block' }}>
          اطلاعات حساب
        </Typography>
        <Stack spacing={2} component="form" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <TextField
            fullWidth
            size="small"
            label="نام کاربری"
            value={formData.username}
            onChange={(e) => setFormData((p) => ({ ...p, username: e.target.value }))}
            required
            autoComplete="off"
            name="erp-panel-username"
            inputProps={{ autoComplete: 'off' }}
            sx={FIELD_SX}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField fullWidth size="small" label="نام" value={formData.first_name} onChange={(e) => setFormData((p) => ({ ...p, first_name: e.target.value }))} sx={FIELD_SX} />
            <TextField fullWidth size="small" label="نام خانوادگی" value={formData.last_name} onChange={(e) => setFormData((p) => ({ ...p, last_name: e.target.value }))} sx={FIELD_SX} />
          </Box>
          <TextField
            fullWidth
            size="small"
            type="password"
            label={editingUserId ? 'رمز عبور جدید (اختیاری)' : 'رمز عبور'}
            value={formData.password}
            onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
            required={!editingUserId}
            sx={FIELD_SX}
          />
        </Stack>
      </Box>

      <Box>
        <Typography variant="caption" sx={{ color: '#676879', fontWeight: 700, mb: 1.5, display: 'block' }}>
          سطح دسترسی
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small" sx={FIELD_SX}>
            <InputLabel id="role-label">نقش</InputLabel>
            <Select
              labelId="role-label"
              label="نقش"
              value={formData.role}
              MenuProps={companyMenuProps}
              onChange={(e) => setFormData((p) => ({
                ...p,
                role: e.target.value,
                company_ids: e.target.value === 'admin' ? [] : p.company_ids,
                is_super_admin: e.target.value === 'admin' ? p.is_super_admin : false,
              }))}
            >
              <MenuItem value="user">کاربر عادی</MenuItem>
              <MenuItem value="admin">مدیر سیستم</MenuItem>
            </Select>
          </FormControl>

          {formData.role === 'user' ? (
            <FormControl fullWidth size="small" sx={FIELD_SX}>
              <InputLabel id="companies-label" shrink>شرکت‌های مجاز</InputLabel>
              <Select
                labelId="companies-label"
                multiple
                label="شرکت‌های مجاز"
                value={normalizeCompanyIds(formData.company_ids)}
                onChange={(e) => setFormData((p) => ({ ...p, company_ids: normalizeCompanyIds(e.target.value) }))}
                renderValue={(sel) => getCompanyNames(sel).join('، ') || 'انتخاب شرکت'}
                MenuProps={companyMenuProps}
              >
                {companies.length === 0 ? (
                  <MenuItem disabled value="">شرکتی ثبت نشده</MenuItem>
                ) : companies.map((c) => {
                  const id = Number(c.id);
                  const checked = normalizeCompanyIds(formData.company_ids).includes(id);
                  return (
                    <MenuItem key={id} value={id} sx={{ gap: 1 }}>
                      <Checkbox checked={checked} size="small" sx={{ p: 0.5, pointerEvents: 'none' }} />
                      <ListItemText primary={c.name} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2, py: 0.5 }}>
              مدیر به همه شرکت‌ها دسترسی دارد.
            </Alert>
          )}

          {formData.role === 'admin' && (
            <Box sx={{
              p: 2, borderRadius: 2, bgcolor: '#e7f4f2',
              border: '1px solid #e5f4ff',
            }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={Boolean(formData.is_super_admin)}
                    onChange={(e) => setFormData((p) => ({ ...p, is_super_admin: e.target.checked }))}
                    sx={{ color: '#1a335b', '&.Mui-checked': { color: '#1a335b' } }}
                  />
                )}
                label={(
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#323338' }}>سوپر ادمین</Typography>
                    <Typography variant="caption" sx={{ color: '#676879' }}>
                      مشاهده همه سوابق عملیات سیستم (ثبت، ویرایش، بایگانی، کاربران و...)
                    </Typography>
                  </Box>
                )}
                sx={{ alignItems: 'flex-start', m: 0 }}
              />
            </Box>
          )}
        </Stack>
      </Box>

      <Box sx={{
        p: 2, borderRadius: 2, bgcolor: '#f7f8fa',
        border: '1px solid #e6e9ef',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#323338' }}>وضعیت حساب</Typography>
          <Typography variant="caption" sx={{ color: '#676879' }}>کاربر غیرفعال نمی‌تواند وارد شود</Typography>
        </Box>
        <Switch
          checked={formData.is_active}
          onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))}
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#0073ea' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#0073ea' } }}
        />
      </Box>
    </Stack>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={48} sx={{ color: '#00a86a' }} />
        <Typography color="text.secondary">در حال بارگذاری کاربران...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #f7f8fa 0%, #e5f4ff 50%, #f7f8fa 100%)',
      pb: 4,
    }}>
      {/* Hero */}
      <Box sx={{
        mt: 0, mb: 3, p: { xs: 2.5, md: 3.5 },
        borderRadius: 4,
        background: '#ffffff',
        color: 'white',
        boxShadow: '0 20px 60px rgba(0, 168, 106, 0.35)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -40, left: -40, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>مدیریت کاربران</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              ایجاد، ویرایش و تخصیص دسترسی شرکت به کاربران
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openPanel()}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(8px)',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          >
            کاربر جدید
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2.5, position: 'relative', zIndex: 1 }}>
          <Chip label={`${fmt(stats.total)} کاربر`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 600 }} />
          <Chip label={`${fmt(stats.admins)} مدیر`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 600 }} />
          <Chip label={`${fmt(stats.active)} فعال`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 600 }} />
        </Box>
      </Box>

      {/* KPI */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { icon: <GroupsIcon />, label: 'کل کاربران', value: fmt(stats.total), color: KPI_COLORS[0] },
          { icon: <AdminIcon />, label: 'مدیران', value: fmt(stats.admins), color: KPI_COLORS[1] },
          { icon: <VerifiedUserIcon />, label: 'فعال', value: fmt(stats.active), color: KPI_COLORS[2] },
          { icon: <PersonOffIcon />, label: 'غیرفعال', value: fmt(stats.inactive), color: KPI_COLORS[3] },
        ].map((kpi, i) => (
          <Grid key={i} size={{ xs: 6, md: 3 }}>
            <KpiCard {...kpi} />
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Card sx={{ ...CARD_SX, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 2,
            '& .MuiTab-root': { fontWeight: 700, minHeight: 52 },
            '& .Mui-selected': { color: '#0073ea' },
            '& .MuiTabs-indicator': { bgcolor: '#0073ea', height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          <Tab value="users" label="کاربران" icon={<GroupsIcon />} iconPosition="start" />
          {currentUser?.is_super_admin && (
            <Tab value="audits" label="سوابق عملیات" icon={<HistoryIcon />} iconPosition="start" />
          )}
        </Tabs>
      </Card>

      {activeTab === 'users' && (
      <>
      {/* Filters */}
      <Card sx={{ ...CARD_SX, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
            <TextField
              size="small"
              placeholder="جستجو نام، نام کاربری..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
              name="erp-user-search-filter"
              id="erp-user-search-filter"
              type="search"
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                spellCheck: 'false',
                role: 'search',
              }}
              sx={{ flex: 1, minWidth: 220, ...FIELD_SX }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#c5c7d0', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {hasActiveFilters && (
                <Chip
                  label="پاک کردن فیلترها"
                  size="small"
                  onDelete={clearFilters}
                  onClick={clearFilters}
                  sx={{ fontWeight: 700, bgcolor: '#fde8eb', color: '#c9364a' }}
                />
              )}
              <FilterChip label="همه" active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
              <FilterChip label="فعال" active={statusFilter === 'active'} onClick={() => setStatusFilter('active')} color="#00a86a" />
              <FilterChip label="غیرفعال" active={statusFilter === 'inactive'} onClick={() => setStatusFilter('inactive')} color="#e44258" />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />
              <FilterChip label="مدیر" active={roleFilters.includes('admin')} onClick={() => setRoleFilters((p) => p.includes('admin') ? p.filter((r) => r !== 'admin') : [...p, 'admin'])} />
              <FilterChip label="کاربر" active={roleFilters.includes('user')} onClick={() => setRoleFilters((p) => p.includes('user') ? p.filter((r) => r !== 'user') : [...p, 'user'])} color="#0073ea" />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ display: 'flex', bgcolor: '#f5f6f8', borderRadius: 2, p: 0.5 }}>
                <IconButton size="small" onClick={() => setViewMode('cards')} sx={{ bgcolor: viewMode === 'cards' ? '#fff' : 'transparent', boxShadow: viewMode === 'cards' ? 1 : 0, borderRadius: 1.5 }}>
                  <ViewModuleIcon sx={{ fontSize: 18, color: viewMode === 'cards' ? '#0073ea' : '#c5c7d0' }} />
                </IconButton>
                <IconButton size="small" onClick={() => setViewMode('table')} sx={{ bgcolor: viewMode === 'table' ? '#fff' : 'transparent', boxShadow: viewMode === 'table' ? 1 : 0, borderRadius: 1.5 }}>
                  <TableRowsIcon sx={{ fontSize: 18, color: viewMode === 'table' ? '#0073ea' : '#c5c7d0' }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Users list */}
      <Card sx={CARD_SX}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#323338' }}>
              لیست کاربران
            </Typography>
            <Chip label={`${fmt(filteredUsers.length)} نتیجه`} size="small" sx={{ fontWeight: 700, bgcolor: '#e5f4ff', color: '#0073ea' }} />
          </Box>

          {filteredUsers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <PersonIcon sx={{ fontSize: 48, color: '#cce5ff', mb: 1 }} />
              <Typography sx={{ fontWeight: 700, color: '#676879' }}>کاربری یافت نشد</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openPanel()} sx={{ mt: 2, borderRadius: 2 }}>
                افزودن کاربر
              </Button>
            </Box>
          ) : viewMode === 'cards' ? (
            <>
            <Box sx={pagedUsers.length <= 2
              ? { display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }
              : USER_CARDS_GRID
            }>
              {pagedUsers.map((u) => (
                <Box key={u.id} sx={{ minWidth: 0, width: '100%' }}>
                  <UserCard user={u} wide={filteredUsers.length <= 2} />
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2 }}>
              <ListPagination
                page={usersPage}
                pageCount={filteredUsers.length === 0 ? 0 : Math.ceil(filteredUsers.length / usersPageSize)}
                totalCount={filteredUsers.length}
                onPageChange={setUsersPage}
              />
            </Box>
            </>
          ) : (
            <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e6e9ef' }}>
              <DataGrid
                autoHeight
                rows={filteredUsers}
                columns={columns}
                getRowId={(row) => row.id}
                pageSizeOptions={[8, 16, 32]}
                initialState={{ pagination: { paginationModel: { pageSize: 8 } } }}
                disableRowSelectionOnClick
                sx={{
                  border: 0,
                  '& .MuiDataGrid-columnHeaders': { bgcolor: '#f7f8fa', fontWeight: 700, color: '#323338' },
                  '& .MuiDataGrid-row:hover': { bgcolor: '#f7f8fa' },
                  '& .MuiDataGrid-cell': { borderColor: '#f5f6f8' },
                  '& .MuiDataGrid-footerContainer': { bgcolor: '#f7f8fa', borderTop: '1px solid #e6e9ef' },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {activeTab === 'audits' && currentUser?.is_super_admin && (
        <Card sx={CARD_SX}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#323338' }}>
                سوابق عملیات سیستم
              </Typography>
              <Chip label={`${fmt(auditTotal)} رکورد`} size="small" sx={{ fontWeight: 700, bgcolor: '#e5f4ff', color: '#0073ea' }} />
            </Box>

            <TextField
              size="small"
              placeholder="جستجو در سوابق..."
              value={auditQuery}
              onChange={(e) => setAuditQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') fetchAuditLogs(0, e.target.value);
              }}
              sx={{ mb: 2, minWidth: 280, ...FIELD_SX }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#c5c7d0' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => fetchAuditLogs(0, auditQuery)} sx={{ fontWeight: 700 }}>
                      جستجو
                    </Button>
                  </InputAdornment>
                ),
              }}
            />

            {auditLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#00a86a' }} />
              </Box>
            ) : flattenedAuditRows.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <HistoryIcon sx={{ fontSize: 48, color: '#cce5ff', mb: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#676879' }}>سابقه‌ای یافت نشد</Typography>
              </Box>
            ) : (
              <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e6e9ef' }}>
                <Box sx={{ ...TABLE_HEADER_BOX_SX, overflowX: 'auto' }}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: AUDIT_GRID_COLS,
                    gap: 1.5,
                    alignItems: 'center',
                    px: 1,
                    minWidth: 1400,
                  }}>
                    {['زمان', 'کاربر', 'عملیات', 'شماره سند', 'شرکت', 'محصول', 'نوع سند', 'محل', 'شیفت', 'تاریخ', 'مقدار (کیلو)', 'واحد', 'ارجاع به حواله', 'توضیحات', 'عیار'].map((col) => (
                      <Typography key={col} sx={TABLE_HEAD_CELL_SX}>{col}</Typography>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
                  {flattenedAuditRows.map((row, index) => {
                    const e = row.entry;
                    const otherInfo = formatOtherAuditInfo(row);
                    return (
                      <Box
                        key={row.rowKey}
                        sx={{
                          ...tableRowSx(index),
                          display: 'grid',
                          gridTemplateColumns: AUDIT_GRID_COLS,
                          gap: 1.5,
                          alignItems: 'center',
                          px: 1,
                          minWidth: 1400,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#676879', fontWeight: 600, textAlign: 'center' }}>
                          {row.created_at ? formatDateTime(row.created_at) : '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#323338', fontWeight: 600, textAlign: 'center' }}>
                          {row.username || '—'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Chip size="small" label={row.action_label} sx={{ fontWeight: 700, bgcolor: '#e5f4ff', color: '#0073ea', fontSize: '0.7rem' }} />
                        </Box>
                        {e ? (
                          <>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>{e.documentNumber}</Typography>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>{e.company}</Typography>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>{e.product}</Typography>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>{e.documentType}</Typography>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>{e.location}</Typography>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>{e.shift}</Typography>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>
                              {e.date ? formatJalaliDateDisplay(e.date) : '—'}
                            </Typography>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>{e.quantity}</Typography>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>{e.unit}</Typography>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>{e.referenceToWaybill || '—'}</Typography>
                            <Box sx={{ textAlign: 'center' }}>
                              {e.description ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => { setAuditDescText(e.description); setAuditDescOpen(true); }}
                                  sx={{ fontSize: '0.7rem', py: 0.25, borderColor: '#cce5ff', color: '#0073ea' }}
                                >
                                  مشاهده
                                </Button>
                              ) : '—'}
                            </Box>
                            <Typography sx={{ textAlign: 'center', color: '#323338', fontWeight: 500, fontSize: '0.85rem' }}>
                              {e.assayPercent != null ? e.assayPercent : '—'}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography sx={{ textAlign: 'center', color: '#c5c7d0', fontSize: '0.85rem' }}>—</Typography>
                            <Typography sx={{
                              textAlign: 'center',
                              color: '#676879',
                              fontSize: '0.82rem',
                              fontWeight: 500,
                              gridColumn: 'span 10',
                              px: 1,
                            }}>
                              {otherInfo}
                            </Typography>
                            <Typography sx={{ textAlign: 'center', color: '#c5c7d0', fontSize: '0.85rem' }}>—</Typography>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <ListPagination
                  page={auditPage}
                  pageCount={auditTotal === 0 ? 0 : Math.ceil(auditTotal / 50)}
                  totalCount={auditTotal}
                  onPageChange={(next) => fetchAuditLogs(next, auditQuery)}
                  disabled={auditLoading}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={auditDescOpen} onClose={() => setAuditDescOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>توضیحات</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-wrap', color: '#323338' }}>{auditDescText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDescOpen(false)} sx={{ fontWeight: 700 }}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* Side panel — add/edit */}
      <Drawer
        key={editingUserId ?? 'new-user'}
        anchor="right"
        open={panelOpen}
        onClose={closePanel}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 440 },
            bgcolor: '#fff',
            boxShadow: '-8px 0 40px rgba(50, 51, 56,0.12)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{
          p: 2.5,
          background: '#00a86a',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {editingUserId ? 'ویرایش کاربر' : 'کاربر جدید'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {editingUser
                ? `در حال ویرایش: ${editingUser.username} (${editingUser.first_name || ''} ${editingUser.last_name || ''})`.trim()
                : 'اطلاعات کاربر را وارد کنید'}
            </Typography>
          </Box>
          <IconButton onClick={closePanel} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          {renderFormFields()}
        </Box>

        <Box sx={{
          p: 2.5, borderTop: '1px solid #e6e9ef',
          display: 'flex', gap: 1.5,
          bgcolor: '#f7f8fa',
        }}>
          <Button fullWidth variant="outlined" onClick={closePanel} sx={{ borderRadius: 2, fontWeight: 700, py: 1.2 }}>
            انصراف
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              py: 1.2,
              background: '#00a86a',
              boxShadow: '0 4px 14px rgba(0,168,106,0.35)',
            }}
          >
            {saving ? 'در حال ذخیره...' : editingUserId ? 'ذخیره تغییرات' : 'ایجاد کاربر'}
          </Button>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModernDashboard;
